import { useState } from 'react';
import { Visual, FeedbackItem } from '../../consultantTypes';
import { CheckCircle, XCircle, Clock, Check, X, Maximize2, Upload, AlertCircle } from 'lucide-react';
import { UploadNewVersionModal } from './UploadNewVersionModal';

interface VisualItemCardProps {
  visual: Visual;
  onToggleSelect: (id: string) => void;
  disabled: boolean;
  onUploadNewVersion?: (file: File, resolvedFeedbackIds: string[]) => Promise<void>;
}

export function VisualItemCard({ visual, onToggleSelect, disabled, onUploadNewVersion }: VisualItemCardProps) {
  const [showPreview, setShowPreview] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);

  const feedbackItems = visual.feedbackItems || [];
  const openFeedbackCount = feedbackItems.filter(f => f.status === 'open').length;
  const resolvedFeedbackCount = feedbackItems.filter(f => f.status === 'resolved').length;
  const getStatusBadge = () => {
    if (visual.status === 'UPLOADED') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">
          <Clock className="w-3 h-3" />
          Te reviewen
        </span>
      );
    }

    if (visual.status === 'APPROVED') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200">
          <CheckCircle className="w-3 h-3" />
          Goedgekeurd
        </span>
      );
    }

    if (visual.status === 'REJECTED') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-50 text-red-700 border border-red-200">
          <XCircle className="w-3 h-3" />
          Afgekeurd
        </span>
      );
    }
  };

  return (
    <>
      <div className={`bg-white rounded-2xl shadow-md overflow-hidden border-2 transition-all ${
        visual.selected ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
      }`}>
        <div className="aspect-video bg-gray-100 relative group cursor-pointer" onClick={() => setShowPreview(true)}>
          <img
            src={visual.url}
            alt={visual.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-opacity flex items-center justify-center">
            <Maximize2 className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
          </div>
        </div>

      <div className="p-5">
        <div className="flex items-start justify-between mb-3">
          <h3 className="font-semibold text-gray-900 text-sm">{visual.title}</h3>
          {getStatusBadge()}
        </div>

        {feedbackItems.length > 0 && (
          <div className="mb-3 space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold text-gray-700">
                Feedback ({openFeedbackCount} open, {resolvedFeedbackCount} verwerkt)
              </p>
              {visual.currentVersion && visual.currentVersion > 1 && (
                <span className="text-xs text-gray-500">v{visual.currentVersion}</span>
              )}
            </div>

            <div className="space-y-2 max-h-40 overflow-y-auto">
              {feedbackItems.map((feedback) => (
                <div
                  key={feedback.id}
                  className={`p-2 rounded-lg text-xs ${
                    feedback.status === 'resolved'
                      ? 'bg-green-50 border border-green-200'
                      : 'bg-red-50 border border-red-200'
                  }`}
                >
                  <div className="flex items-start gap-2">
                    {feedback.status === 'resolved' ? (
                      <CheckCircle className="w-3 h-3 text-green-600 mt-0.5 flex-shrink-0" />
                    ) : (
                      <AlertCircle className="w-3 h-3 text-red-600 mt-0.5 flex-shrink-0" />
                    )}
                    <p className={feedback.status === 'resolved' ? 'text-green-700' : 'text-red-700'}>
                      {feedback.feedbackText}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {visual.hasUpdate && (
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg mb-3">
            <p className="text-xs font-semibold text-blue-800">
              In afwachting van nieuwe beoordeling door klant
            </p>
          </div>
        )}

        {visual.status === 'APPROVED' && feedbackItems.length === 0 && (
          <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-center mb-3">
            <p className="text-sm font-medium text-green-700">Deze visual is goedgekeurd door de klant</p>
          </div>
        )}

        <div className="space-y-2">
          {openFeedbackCount > 0 && onUploadNewVersion && (
            <button
              onClick={() => setShowUploadModal(true)}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg font-medium hover:bg-orange-700 transition-colors"
            >
              <Upload className="w-4 h-4" />
              Upload Nieuwe Versie
            </button>
          )}

          <button
            onClick={() => onToggleSelect(visual.id)}
            disabled={disabled && !visual.selected}
            className={`w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
              visual.selected
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : disabled
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {visual.selected && <Check className="w-4 h-4" />}
            {visual.selected ? 'Geselecteerd' : 'Selecteer'}
          </button>
        </div>
      </div>
    </div>

    {showPreview && (
      <div
        className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
        onClick={() => setShowPreview(false)}
      >
        <button
          onClick={() => setShowPreview(false)}
          className="absolute top-4 right-4 p-2 bg-white rounded-full hover:bg-gray-100 transition-colors"
        >
          <X className="w-6 h-6 text-gray-900" />
        </button>
        <img
          src={visual.url}
          alt={visual.title}
          className="max-w-full max-h-full object-contain"
          onClick={(e) => e.stopPropagation()}
        />
      </div>
    )}

    {showUploadModal && onUploadNewVersion && (
      <UploadNewVersionModal
        visualId={visual.id}
        visualTitle={visual.title}
        feedbackItems={feedbackItems}
        onClose={() => setShowUploadModal(false)}
        onUpload={onUploadNewVersion}
      />
    )}
    </>
  );
}
